import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import BookListLayout from '@/layouts/guide-book-list';
import GuideDocsLayout from '@/layouts/guide-docs';
import LoginQQ from '@/login/loginQQ';
import InvitedRecord from '@/pages/auth/invited/invitedRecord';
import ChapterSceneDiffView from '@/pages/diff';
import Docs from '@/pages/docs';
import NovelHomeInfo from '@/pages/novel/home/info';
import UiLayout from '@/ui';

import {
  AI_TOWN_EDITOR,
  BASE_NAME,
  BUDGET_MANAGE,
  CONTRACT,
  CREATE_MATERIAL_INFO,
  CREATE_PLAZA_INFO,
  CREATE_PROMOTION_INFO,
  CREATE_SPACE_INFO,
  EDITOR,
  IMAGE_EDIT,
  INCENTIVE_GRANT,
  MANAGE,
  MANAGE_MATERIAL_LIST,
  MANAGE_PLAZA_LIST,
  MANAGE_PROMOTION_LIST,
  MANAGE_SPACE_LIST,
  NOVEL_CONFIG,
  NOVEL_HOME_INFO,
  NOVEL_HOME_LIST,
  NOVEL_TOOL,
  PIC_BOOK_EDITOR,
  PIC_BOOK_HOME,
  PIC_BOOK_IMAGE_LIST,
  PIC_BOOK_TEXT_LIST,
  STORYLINE,
  UGC_BOT_LIST,
  UGC_NOVEL_LIST,
  UGC_STORY_LIST,
  WEB_ADMIN_HOME,
} from './const';
import { renderRoutes } from './render';

const UgcNovelList = lazy(() => import('@/pages/novel/ugcList'));
const GuidePlazaLayout = lazy(() => import('@/layouts/guide-plaza'));
const GuidePromotionLayout = lazy(() => import('@/layouts/guide-promotion'));
const GuideSpaceLayout = lazy(() => import('@/layouts/guide-space'));
const GuideUgcEditorLayout = lazy(() => import('@/layouts/guide-ugc-editor'));
const UGCListLayout = lazy(() => import('@/layouts/guide-ugc-list'));
const LoginAuth = lazy(() => import('@/login/loginAuth'));
const LaunchApp = lazy(() => import('@/pages/launchApp'));
const ErrorBoundary = lazy(() => import('@/pages/error'));
const Auth403 = lazy(() => import('@/pages/error/403'));
const Auth404 = lazy(() => import('@/pages/error/404'));
const WoaAuth403 = lazy(() => import('@/pages/error/woa403'));
const Contract = lazy(() => import('@/pages/finance/contract'));
const BasicLayout = lazy(() => import('@/layouts/basic'));
const GuideMaterialLayout = lazy(() => import('@/layouts/guide-asset'));
const ListLayout = lazy(() => import('@/layouts/guide-list'));
const ManageLayout = lazy(() => import('@/layouts/guide-manage'));
const ManageMaterialLayout = lazy(() => import('@/layouts/guide-manage-asset'));
const ImageEdit = lazy(() => import('@/editor/imageEditor'));
const UgcBotList = lazy(() => import('@/pages/bot/ugcList'));
const Home = lazy(() => import('@/pages/home'));
const MaterialInfo = lazy(() => import('@/pages/material'));
const MaterialList = lazy(() => import('@/pages/material/list'));
const PlazaInfo = lazy(() => import('@/pages/plaza'));
const PlazaList = lazy(() => import('@/pages/plaza/list'));
const PromotionInfo = lazy(() => import('@/pages/promotion'));
const PromotionList = lazy(() => import('@/pages/promotion/list'));
const SpaceInfo = lazy(() => import('@/pages/space/info'));
const SpaceList = lazy(() => import('@/pages/space/list'));
const SpaceStoryList = lazy(() => import('@/pages/space/storyList'));
const UGCStoryList = lazy(() => import('@/pages/story/ugcList'));
const UgcHome = lazy(() => import('@/pages/ugcHome'));
const Storyline = lazy(() => import('@/editor/flowEditor/control/map/storyLineMap'));
const NovelTool = lazy(() => import('@/pages/novel/tool'));
const NovelHome = lazy(() => import('@/pages/novel/home/list'));
const NovelConfig = lazy(() => import('@/pages/novel/config'));
const AiTownEditor = lazy(() => import('@/pages/aiTown/editor'));
const IncentiveGrant = lazy(() => import('@/pages/growth/incentiveGrant'));
const BudgetManage = lazy(() => import('@/pages/growth/budgetManage'));
const GuideNovelLayout = lazy(() => import('@/layouts/guide-novel'));
const PicBookHome = lazy(() => import('@/pages/picBook/home'));
const PicBookEditor = lazy(() => import('@/pages/picBook/editor'));
const PicBookManage = lazy(() => import('@/pages/picBook/manage'));

export const Routes = createBrowserRouter(
  renderRoutes([
    {
      path: '/h5/launchApp',
      element: <LaunchApp />,
    },
    {
      path: '/authlogin',
      element: <LoginAuth />,
    },
    {
      path: '/ui',
      element: <BasicLayout />,
      children: [
        {
          path: '/ui/home',
          element: <UiLayout />,
        },
      ],
    },
    {
      path: '/studio/qqcallback',
      element: <LoginQQ />,
    },
    {
      path: '/',
      errorElement: <ErrorBoundary />,
      element: <BasicLayout />,
      children: [
        {
          path: WEB_ADMIN_HOME,
          element: <UgcHome />,
        },
        {
          path: CONTRACT,
          element: <Contract />,
        },
        {
          path: `${EDITOR}`,
          element: <GuideUgcEditorLayout />,
        },
        {
          path: PIC_BOOK_HOME,
          element: <PicBookHome />,
        },
        {
          path: PIC_BOOK_EDITOR,
          element: <PicBookEditor />,
        },
        {
          path: '/list',
          element: <ListLayout />,
          children: [
            {
              path: '/list/space',
              element: <SpaceList />,
            },
          ],
        },
        {
          path: '/studio',
          element: <UGCListLayout />,
          children: [
            {
              path: UGC_STORY_LIST,
              element: <UGCStoryList />,
            },
            {
              path: UGC_BOT_LIST,
              element: <UgcBotList />,
            },

          ],
        },
        {
          path: '/404',
          element: <Auth404 />,
        },
        {
          path: '/403',
          element: <Auth403 />,
        },
      ],
    },
    {
      path: '/documents',
      errorElement: <ErrorBoundary />,
      element: <GuideDocsLayout />,
      children: [
        {
          path: '/documents/home',
          element: <Docs />,
        },
      ],
    },
    {
      path: STORYLINE,
      element: <Storyline />,
    },
    {
      path: IMAGE_EDIT,
      element: <ImageEdit />,
    },
  ]),
  {
    basename: BASE_NAME,
  },
);


export const ManageRoutes = createBrowserRouter(
  renderRoutes([
    {
      path: '/',
      element: <BasicLayout />,
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: WEB_ADMIN_HOME,
          element: <Home />,
        },
        {
          path: '/diffView',
          element: <ChapterSceneDiffView />,
        },
        {
          path: CONTRACT,
          element: <Contract />,
        },
        {
          path: MANAGE,
          element: <GuideNovelLayout />,
          children: [
            {
              path: NOVEL_TOOL,
              element: <NovelTool />,
            },
            {
              path: NOVEL_HOME_LIST,
              element: <NovelHome />,
            },
            {
              path: NOVEL_HOME_INFO,
              element: <NovelHomeInfo />,
            },
            {
              path: NOVEL_CONFIG,
              element: <NovelConfig />,
            },
          ],
        },
        {
          path: AI_TOWN_EDITOR,
          element: <GuideNovelLayout />,
          children: [
            {
              path: AI_TOWN_EDITOR,
              element: <AiTownEditor />,
            },
          ],
        },
        {
          path: MANAGE,
          element: <GuideNovelLayout />,
          children: [
            {
              path: INCENTIVE_GRANT,
              element: <IncentiveGrant />,
            },
            {
              path: BUDGET_MANAGE,
              element: <BudgetManage />,
            },
          ],
        },
        {
          path: '/studio',
          element: <UGCListLayout />,
          children: [
            {
              path: UGC_STORY_LIST,
              element: <UGCStoryList />,
            },
            {
              path: UGC_BOT_LIST,
              element: <UgcBotList />,
            },
            {
              path: UGC_NOVEL_LIST,
              element: <UgcNovelList />,
            },
          ],
        },
        {
          path: '/picBook',
          element: <BookListLayout />,
          children: [
            {
              path: PIC_BOOK_TEXT_LIST,
              element: <PicBookManage />,
            },
            {
              path: PIC_BOOK_IMAGE_LIST,
              element: <PicBookManage />,
            },
          ],
        },
        {
          path: `${EDITOR}`,
          element: <GuideUgcEditorLayout />,
        },
        {
          path: MANAGE,
          element: <GuideSpaceLayout />,
          children: [
            {
              path: `${CREATE_SPACE_INFO}/:id?`,
              element: <SpaceInfo />,
            },
          ],
        },
        {
          path: MANAGE,
          element: <GuideSpaceLayout />,
          children: [
            {
              path: `${CREATE_SPACE_INFO}/:id?`,
              element: <SpaceInfo />,
            },
          ],
        },
        {
          path: MANAGE,
          element: <GuidePromotionLayout />,
          children: [
            {
              path: `${CREATE_PROMOTION_INFO}/:id?`,
              element: <PromotionInfo />,
            },
          ],
        },
        {
          path: MANAGE,
          element: <GuidePlazaLayout />,
          children: [
            {
              path: `${CREATE_PLAZA_INFO}/:id?`,
              element: <PlazaInfo />,
            },
          ],
        },
        {
          path: MANAGE,
          element: <GuideMaterialLayout />,
          children: [
            {
              path: `${CREATE_MATERIAL_INFO}/:id?`,
              element: <MaterialInfo />,
            },
          ],
        },
        {
          path: '/list',
          element: <ListLayout />,
          children: [
            {
              path: '/list/space',
              element: <SpaceList />,
            },
          ],
        },
        {
          path: MANAGE,
          element: <ManageLayout />,
          children: [
            {
              path: MANAGE_SPACE_LIST,
              element: <SpaceList />,
            },
            {
              path: MANAGE_PROMOTION_LIST,
              element: <PromotionList />,
            },
            {
              path: MANAGE_PLAZA_LIST,
              element: <PlazaList />,
            },
          ],
        },
        {
          path: MANAGE_MATERIAL_LIST,
          element: <ManageMaterialLayout />,
          children: [
            {
              path: MANAGE_MATERIAL_LIST,
              element: <MaterialList />,
            },
          ],
        },
        {
          path: '/manage/space/list/story/:spaceId?',
          element: <SpaceStoryList />,
        },
        {
          path: '/invitedRecord',
          element: <InvitedRecord />,
        },
        {
          path: '/404',
          element: <Auth404 />,
        },
        {
          path: '/403',
          element: <Auth403 />,
        },
        {
          path: '/woa403',
          element: <WoaAuth403 />,
        },
      ],
    },
    {
      path: STORYLINE,
      element: <Storyline />,
    },
  ]),
  {
    basename: BASE_NAME,
  },
);
